/**
 * API utility for fetching a managed user's availables payload.
 * Used to resolve certificate details from backend data.
 */
import { getUserAvailables } from '@/lib';
import { useCallback } from 'react';
/**
 * Logging + mapping utilities.
 * - `consoleLog.warn` keeps warning output consistent across the app.
 * - `mapApiAvailablesToFrontend` normalizes backend payload to frontend cert entities.
*/
import { consoleLog, mapApiAvailablesToFrontend } from '@/utils';
/**
 * Shared sidebar payload shape from global store.
 * The certificate sidebar reuses the same structural contract.
 */
import type { IGlobalStateSidebarScorecard } from '@/store/types';
/** Domain entities used by this hook's input and matching logic. */
import type { IManagedUserSession, IManagerProduct } from '@/types';

/**
 * Injectable dependency contract for this hook.
 *
 * This allows tests to provide deterministic mock implementations while keeping
 * production usage wired to real APIs/mappers/loggers.
 */
interface UseManagedUserCertificateHandlerDependencies {
  /** Fetches raw availables for the managed user id. */
  getUserAvailables: typeof getUserAvailables;
  /** Error logger abstraction used in not-found/fetch-failure paths. */
  logError: (...args: unknown[]) => void;
  /** Warning logger abstraction used in guard-clause exits. */
  logWarn: (...args: unknown[]) => void;
  /** Maps raw availables payload to frontend certification records. */
  mapApiAvailablesToFrontend: typeof mapApiAvailablesToFrontend;
}

/**
 * Default runtime dependencies used in production.
 */
const defaultDependencies: UseManagedUserCertificateHandlerDependencies = {
  getUserAvailables,
  logError: (...args) => console.error(...args),
  logWarn: (...args) => consoleLog.warn(...args),
  mapApiAvailablesToFrontend,
};

/**
 * Input params for `useManagedUserCertificateHandler`.
 */
export interface UseManagedUserCertificateHandlerParams {
  /** Optional dependency override (mainly for unit tests). */
  dependencies?: UseManagedUserCertificateHandlerDependencies;
  /** Display name to show in certificate sidebar once data is loaded. */
  managedUserFullName: string;
  /** Managed user id required by the availables API call. */
  managedUserId?: string | null;
  /** Product list used to sync manager active product by certification id. */
  products: IManagerProduct[];
  /** Store action to set/clear the currently selected manager product. */
  setManagerActiveProduct: (product: IManagerProduct | null) => void;
  /** Store action to update the certificate sidebar payload. */
  setSidebarCertificate: (sidebarCertificate: IGlobalStateSidebarScorecard | null) => void;
}

export interface UseManagedUserCertificateHandlerResult {
  /**
   * Handles "View Certificate" user interaction.
   *
   * Responsibilities:
   * 1) Validate input and required context
   * 2) Reset sidebar to loading/blank state
   * 3) Fetch availables data
   * 4) Resolve target certificate by `certificationId`
   * 5) Sync active product when matching product is found
   * 6) Hydrate sidebar payload or report errors
   */
  handleViewCertificateClick: (record: IManagedUserSession) => Promise<void>;
}

/**
 * Encapsulates certificate sidebar loading/selection flow in a standalone,
 * dependency-injectable hook for easier unit testing.
 */
export const useManagedUserCertificateHandler = ({
  dependencies = defaultDependencies,
  managedUserFullName,
  managedUserId,
  products,
  setManagerActiveProduct,
  setSidebarCertificate,
}: UseManagedUserCertificateHandlerParams): UseManagedUserCertificateHandlerResult => {
  /**
   * Memoized click handler used by tab tables/cards.
   *
   * Memoization keeps handler identity stable unless one of its runtime
   * dependencies changes.
   */
  const handleViewCertificateClick = useCallback(
    async (record: IManagedUserSession) => {
      // Guard: no record passed from caller.
      if (!record) {
        dependencies?.logWarn('handleViewCertificateClick called without a record');
        return;
      }

      // Guard: missing certification id means we cannot resolve a certificate.
      if (!record.certificationId) {
        dependencies?.logWarn('handleViewCertificateClick called without a certificationId');
        return;
      }

      // Normalize id once and reuse for logs, comparisons, and UI payload derivation.
      const certificationId = record.certificationId.toString();

      // Guard: backend call requires managed user id.
      if (!managedUserId) {
        dependencies?.logWarn(
          `handleViewCertificateClick(${certificationId}) but managedUserId is undefined`,
        );
        return;
      }

      // Reset sidebar immediately to avoid stale content flash while loading.
      setSidebarCertificate({
        active: true,
        certification: null,
        score: null,
        simulation: null,
        title: null,
        userName: null,
      });

      try {
        // Fetch availables once, then derive the target certificate client-side.
        const apiAvailables = await dependencies?.getUserAvailables(managedUserId);

        // Convert backend payload to frontend certification entities and locate
        // the certificate corresponding to the clicked record.
        const formattedCertification =
          dependencies
            ?.mapApiAvailablesToFrontend({ apiAvailables, apiScorecards: [] })
            ?.find((cert) => cert.id.toString() === record.certificationId?.toString()) || null;

        if (formattedCertification) {
          // Keep global active product in sync with selected certificate when possible.
          const product = products.find((c) => c?.certificationId.toString() === certificationId);
          if (product) setManagerActiveProduct(product);

          // Hydrate sidebar with final certificate payload for rendering.
          setSidebarCertificate({
            active: true,
            certification: formattedCertification,
            score: null,
            simulation: null,
            title: null,
            userName: managedUserFullName,
          });
        } else {
          // Explicit not-found signal for diagnostics.
          dependencies?.logError('Certificate not found for certificationId:', certificationId);
        }
      } catch (error) {
        // Network/parse/etc. failures for availables fetch or mapping.
        dependencies?.logError(
          'Error fetching certificate for certificationId',
          certificationId,
          error,
        );
      }
    },
    [
      // =================================================================
      // Runtime dependencies
      // (changes to any of these will re-create the handler)
      // ===================================================================
      dependencies,
      managedUserFullName,
      managedUserId,
      products,
      setManagerActiveProduct,
      setSidebarCertificate,
    ],
  );

  // =================================================================
  // Stable hook API
  // =================================================================
  return {
    handleViewCertificateClick,
  };
};
